import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import * as yup from "yup";

import MyButton from "../../components/MyButton";
import MyTextField from "../../components/inputs/MyTextField";
import MyTextArea from "../../components/inputs/MyTextArea";
import { AuthContext } from "../../context/AuthProvider";
import { useBoardItem } from "../../hooks/useBoardItem";
import MyTable from "../../components/MyTable";
import { SiteContext } from "../../context/SiteProvider";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../constants/permission";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../components/CheckRoleAndPermission";

const ContactUs = ({ boardCode }) => {
  const { isAdmin, isAuthenticated, profile, roleCode } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const { useGetBoardItems, useGetBoard, createBoardItemMutation } =
    useBoardItem();
  const { setLoadingScreen } = useContext(SiteContext);

  const headers = [
    {
      id: "body",
      title: "문의 내용",
      style: {
        width: 350,
        minWidth: 200,
        textAlign: "center",
      },
    },
    {
      id: "author",
      title: "작성자",
      style: {
        width: 120,
        minWidth: 120,
        textAlign: "center",
      },
    },
    {
      id: "createdDate",
      title: "작성일",
      style: {
        width: 100,
        minWidth: 100,
        textAlign: "center",
      },
    },
  ];

  const manager = {
    name: "담당자",
    phone: "+82-2-3010-3390",
    email: "-",
  };

  const kcdh = {
    address:
      "[05505] 서울특별시 송파구 올림픽로43길 88 서울아산병원 신관 6층 신생아과 의국",
    phone: "+82-2-3010-3390",
    fax: "+82-2-3010-6978",
    website: "k-cdh.org",
  };

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItems({
    size: board?.pageSize,
    page,
    sort: "createdDate,Desc",
    boardCode: board?.boardCode,
    enabled:
      isAdmin &&
      checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.ACCESS,
        nonPermissions,
        isAdmin
      ),
  });

  const initValues = {
    writerName: "",
    email: "",
    body: "",
  };

  const validationSchema = yup.object({
    writerName: yup.string().required("필수 입력 항목입니다."),
    email: yup
      .string()
      .email("이메일 형식이 올바르지 않습니다.")
      .required("필수 입력 항목입니다."),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const paginationProps = useMemo(
    () => ({
      size: board?.pageSize,
      page,
      setPage,
      maxPage,
      total,
    }),
    [board?.pageSize, maxPage, page, total]
  );

  const onSubmit = (data) => {
    if (board.boardCode) {
      setLoadingScreen(true);

      const payload = {
        title: data.title,
        body: data.body,
        additionalItems: {},
        writerName: data.writerName,
        writerEmail: data.email,
        attachedFiles: [],
      };

      createBoardItemMutation.mutate(
        { boardCode: board.boardCode, payload },
        {
          onSuccess: (res) => {
            if (res) {
              methods.reset();
              alert("게시글 작성이 완료되었습니다.");
            } else {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            }
          },
          onError: () => {
            alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
          },
          onSettled: () => {
            setLoadingScreen(false);
          },
        }
      );
    } else {
      alert("게시글 카테고리가 존재하지 않습니다.");
    }
  };

  const mapData = (listData) => {
    setMaxPage(listData.totalPages);
    setTotal(listData.totalElements);

    const newContent = listData.content?.map((item, idx) => ({
      ...item,
      body: (
        <p
          className="overflow-hidden text-ellipsis line-clamp-1 text-gray-dark text-left"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            wordBreak: "break-all",
          }}
        >
          {item.body}
        </p>
      ),
      author: item?.writer?.name,
      createdDate: dayjs(item.createdDate).isValid()
        ? dayjs(item.createdDate).format("YYYY-MM-DD")
        : "-",
    }));

    setContent(newContent);
  };

  useEffect(() => {
    if (!isLoading && data?.data) {
      mapData(data.data);
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (isAuthenticated) {
      methods.setValue("writerName", profile.userName);
      methods.setValue("email", profile.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, profile]);

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.ACCESS,
        nonPermissions,
        isAdmin
      )
    )
      alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return isAdmin ? (
    <div>
      <MyTable
        showIndex
        addText="글 작성하기"
        canClick
        headers={headers}
        data={content}
        dataKey="itemNo"
        loading={isLoading}
        paginationProps={paginationProps}
        onRowClick={(row) => {
          if (
            checkRoleAndPermissions(
              roleCode,
              board,
              BOARD_PERMISSION.READ,
              nonPermissions,
              isAdmin
            )
          ) {
            navigate(`/ContactUs/${row.itemNo}`);
          } else {
            alert(getPermissionMessage(BOARD_PERMISSION.READ));
          }
        }}
      />
    </div>
  ) : (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.WRITE}
      boardCode={board?.boardCode}
    >
      <div className="flex flex-wrap gap-x-[4%] gap-y-[20px]">
        <div className="sm:w-[56%] w-full">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <div className="flex flex-wrap mb-5">
                <label
                  htmlFor="writerName"
                  className="md:mt-4 md:w-[100px] w-full font-semibold"
                >
                  이름 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="writerName"
                    name="writerName"
                    placeholder="작성자 이름을 입력해주세요."
                  />
                </div>
              </div>

              <div className="flex flex-wrap mb-5">
                <label
                  htmlFor="email"
                  className="md:mt-4 md:w-[100px] w-full font-semibold"
                >
                  이메일 주소 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="email"
                    name="email"
                    placeholder="이메일 주소를 입력해주세요."
                  />
                </div>
              </div>

              <div className="flex flex-wrap mb-5">
                <label
                  htmlFor="body"
                  className="md:mt-4 md:w-[100px] w-full font-semibold"
                >
                  문의 내용
                </label>
                <div className="flex-1">
                  <MyTextArea
                    id="body"
                    name="body"
                    rows="14"
                    cols="24"
                    resize={false}
                  />
                </div>
              </div>

              <div className="flex mt-5">
                <div className="w-[100px] md:block hidden" />
                <MyButton
                  variant="contained"
                  color="primary"
                  className="flex-1 font-semibold"
                  type="submit"
                  size="small"
                >
                  제출하기
                </MyButton>
              </div>
            </form>
          </FormProvider>
        </div>

        {/* INFO */}
        <div className="sm:w-[40%] w-full bg-[#F9F9FF] rounded-xl lg:p-8 sm:p-5 p-3">
          <div className="flex gap-1 flex-wrap lg:mb-14 mb-10">
            <div className="xl:w-[126px] w-full">
              <h2 className="inline-block pb-1 border-b border-primary font-semibold text-[22px]">
                Manager
              </h2>
            </div>
            <div className="flex-1 flex flex-col lg:gap-4 gap-3 pt-1">
              <dl>
                <dt className="mb-1 font-medium text-sm">Name</dt>
                <dd className="text-gray-dark">{manager.name}</dd>
              </dl>
              <dl>
                <dt className="mb-1 font-medium text-sm">Tel</dt>
                <dd className="text-gray-dark">{manager.phone}</dd>
              </dl>
              <dl>
                <dt className="mb-1 font-medium text-sm">E-mail</dt>
                <dd className="text-gray-dark">{manager.email}</dd>
              </dl>
            </div>
          </div>

          <div className="flex gap-1 flex-wrap">
            <div className="xl:w-[126px] w-full">
              <div className="inline-block pb-1 border-primary border-b">
                <h2 className="font-semibold text-[22px]">K-CDH</h2>
                <span
                  className="block text-xs text-gray-dark font-medium"
                  style={{
                    marginTop: "-4px",
                  }}
                >
                  Secretariat
                </span>
              </div>
            </div>
            <div className="flex-1 flex flex-col lg:gap-4 gap-3 pt-1">
              <dl>
                <dt className="mb-1 font-medium text-sm">Address</dt>
                <dd className="text-gray-dark">{kcdh.address}</dd>
              </dl>
              <dl>
                <dt className="mb-1 font-medium text-sm">Tel</dt>
                <dd className="text-gray-dark">{kcdh.phone}</dd>
              </dl>
              <dl>
                <dt className="mb-1 font-medium text-sm">Fax</dt>
                <dd className="text-gray-dark">{kcdh.fax}</dd>
              </dl>
              <dl>
                <dt className="mb-1 font-medium text-sm">Website</dt>
                <dd className="text-gray-dark">{kcdh.website}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </CheckRoleAndPermissionDisplay>
  );
};

export default ContactUs;
